import React, { useEffect, useState } from 'react';
import {
  Box,
  Heading,
  Text,
  Button,
  Image,
  Flex,
  SimpleGrid,
  Container,
  Badge,
  HStack,
  VStack,
  Icon,
} from '@chakra-ui/react';
import { FiCheck, FiArrowRight, FiBriefcase, FiCalendar, FiClock, FiFileText, FiShield } from 'react-icons/fi';
import { apiUrl } from '../ApiUrl/ApiUrl';
import Slider from 'react-slick';
import { Link, NavLink } from 'react-router-dom';

function LMS() {
  const [fashion, setFashion] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const settings = {
    infinite: true,
    speed: 500,
    slidesToShow: 4,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 3000,
    pauseOnHover: true,
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 3,
        },
      },
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 2,
        },
      },
      {
        breakpoint: 480,
        settings: {
          slidesToShow: 1,
        },
      },
    ],
  };

  const getFashion = () => {
    setLoading(true);
    fetch(`${apiUrl}/public/allbooks`)
      .then((res) => res.json())
      .then((res) => setFashion(res))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    getFashion();
  }, []);

  return (
    <Box bg="#F8FAFC" minH="100vh" pb={16}>
      {/* Hero Header */}
      <Box
        bgGradient="linear(to-b, #0B132B, #162237)"
        color="white"
        py={{ base: 12, md: 16 }}
        px={{ base: 4, md: 8 }}
        textAlign="center"
      >
        <Container maxW="900px">
          <Badge
            bg="whiteAlpha.200"
            color="#EAB308"
            px={3}
            py={1}
            rounded="full"
            fontSize="xs"
            fontWeight="bold"
            letterSpacing="wider"
            mb={4}
            textTransform="uppercase"
          >
            Chambers ERP & Docket Automation
          </Badge>
          <Heading as="h1" size={{ base: "xl", md: "2xl" }} fontWeight="black" mb={4} letterSpacing="tight">
            Litigation Management Software <br />
            <Text as="span" color="#FACC15">(LMS)</Text>
          </Heading>
          <Text fontSize={{ base: "sm", md: "md" }} color="gray.300" maxW="700px" mx="auto" lineHeight="tall">
            Streamline your case dockets, cause list tracking, hearing schedules, and team briefings across Supreme Court, High Courts, and Indian Tribunals.
          </Text>
        </Container>
      </Box>

      {/* Main Content Showcase */}
      <Container maxW="7xl" px={{ base: 4, md: 8 }} mt={-8}>
        <Box
          bg="white"
          borderRadius="2xl"
          p={{ base: 6, md: 10 }}
          boxShadow="0 10px 30px rgba(0,0,0,0.06)"
          border="1px solid"
          borderColor="gray.100"
        >
          <SimpleGrid columns={{ base: 1, lg: 12 }} spacing={{ base: 8, lg: 10 }} alignItems="center">
            {/* Left Content */}
            <Box gridColumn={{ base: "span 1", lg: "span 7" }}>
              <VStack align="stretch" spacing={6}>
                <Box>
                  <Text fontSize="xs" fontWeight="bold" textTransform="uppercase" color="#EAB308" letterSpacing="wider" mb={2}>
                    Automated Cause List Sync
                  </Text>
                  <Heading as="h2" size="lg" color="#162237" mb={3}>
                    Never Miss an Item Call or Hearing Date
                  </Heading>
                  <Text fontSize="sm" color="gray.600" lineHeight="relaxed">
                    Designed specifically for busy advocates, chambers, and corporate legal departments. LMS automatically tracks multiple court cause lists, maintains client case files, attaches certified orders, manages junior associate tasks, and dispatches automated hearing reminders.
                  </Text>
                </Box>

                {/* Features */}
                <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={3}>
                  <HStack spacing={3} p={3} bg="#F8FAFC" borderRadius="xl">
                    <Icon as={FiBriefcase} color="#162237" boxSize={5} />
                    <Box>
                      <Text fontSize="xs" fontWeight="bold" color="#162237">Master Case Repository</Text>
                      <Text fontSize="11px" color="gray.500">Full client dossier & case diary</Text>
                    </Box>
                  </HStack>
                  <HStack spacing={3} p={3} bg="#F8FAFC" borderRadius="xl">
                    <Icon as={FiCalendar} color="#162237" boxSize={5} />
                    <Box>
                      <Text fontSize="xs" fontWeight="bold" color="#162237">Live Cause List Alerts</Text>
                      <Text fontSize="11px" color="gray.500">Auto-matches advocate name & bar code</Text>
                    </Box>
                  </HStack>
                  <HStack spacing={3} p={3} bg="#F8FAFC" borderRadius="xl">
                    <Icon as={FiClock} color="#162237" boxSize={5} />
                    <Box>
                      <Text fontSize="xs" fontWeight="bold" color="#162237">Limitation Period Watch</Text>
                      <Text fontSize="11px" color="gray.500">Statutory appeal deadline countdowns</Text>
                    </Box>
                  </HStack>
                  <HStack spacing={3} p={3} bg="#F8FAFC" borderRadius="xl">
                    <Icon as={FiShield} color="#162237" boxSize={5} />
                    <Box>
                      <Text fontSize="xs" fontWeight="bold" color="#162237">Role-Based Access</Text>
                      <Text fontSize="11px" color="gray.500">Secure junior & partner segregation</Text>
                    </Box>
                  </HStack>
                </SimpleGrid>

                {/* Pricing Box */}
                <Box
                  p={6}
                  bg="linear-gradient(135deg, #F8FAFC 0%, #EFF6FF 100%)"
                  borderRadius="2xl"
                  border="1px solid"
                  borderColor="blue.100"
                >
                  <Flex justify="space-between" align={{ base: "flex-start", sm: "center" }} direction={{ base: "column", sm: "row" }} gap={4}>
                    <Box>
                      <Badge colorScheme="blue" mb={1} px={2} py={0.5} borderRadius="md" fontSize="xs">
                        Single User Annual Edition
                      </Badge>
                      <Text fontSize="xl" fontWeight="black" color="#162237">
                        ₹20,000 <Text as="span" fontSize="xs" fontWeight="normal" color="gray.500">+ 18% GST / annum</Text>
                      </Text>
                      <Text fontSize="xs" color="gray.500" mt={0.5}>
                        Unlimited case briefs, cloud storage, notifications & circular sync
                      </Text>
                    </Box>

                    <Button
                      as={Link}
                      to="/products/view/704"
                      size="lg"
                      bg="#162237"
                      color="white"
                      rightIcon={<FiArrowRight />}
                      _hover={{ bg: "#0B132B", transform: "translateY(-2px)", boxShadow: "md" }}
                      transition="all 0.2s"
                      px={8}
                    >
                      Subscribe Now
                    </Button>
                  </Flex>
                </Box>
              </VStack>
            </Box>

            {/* Right Mockup */}
            <Box gridColumn={{ base: "span 1", lg: "span 5" }}>
              <Box
                borderRadius="2xl"
                overflow="hidden"
                boxShadow="2xl"
                border="1px solid"
                borderColor="gray.200"
                bg="#162237"
                p={2}
              >
                <Image
                  src="./litigation-management.jpeg"
                  alt="Litigation Management Software"
                  w="full"
                  h="auto"
                  borderRadius="xl"
                  objectFit="cover"
                  fallbackSrc="https://placehold.co/600x400/162237/white?text=LMS+Suite"
                />
              </Box>
            </Box>
          </SimpleGrid>
        </Box>
      </Container>

      {/* Recommended Softwares Slider */}
      <Container maxW="7xl" px={{ base: 4, md: 8 }} mt={16}>
        <HStack justify="space-between" align="flex-end" mb={6}>
          <Box>
            <Text fontSize="xs" fontWeight="bold" textTransform="uppercase" color="#EAB308" letterSpacing="wider">
              Legal Tech Suite
            </Text>
            <Heading size="lg" color="#162237">
              Explore More Specialized Judicial Tools
            </Heading>
          </Box>
          <Button as={Link} to="/software" variant="ghost" color="#162237" rightIcon={<FiArrowRight />} size="sm">
            View All Software
          </Button>
        </HStack>

        <Box className="software-slick-slider">
          <Slider {...settings}>
            {fashion
              .filter((item) => item.maincatname === "software")
              .map((item) => (
                <Box key={item.id} px={2} pb={4}>
                  <Box
                    as={NavLink}
                    to={`/products/view/${item.id}`}
                    display="block"
                    bg="white"
                    p={4}
                    borderRadius="xl"
                    border="1px solid"
                    borderColor="gray.100"
                    boxShadow="sm"
                    _hover={{ transform: "translateY(-4px)", boxShadow: "md", borderColor: "gray.300" }}
                    transition="all 0.2s"
                    h="full"
                  >
                    <Image
                      src={item.image1 || item.image || "./navneetlawstudio.png"}
                      alt={item.bookTitle}
                      h="140px"
                      w="full"
                      objectFit="contain"
                      mb={3}
                      p={2}
                    />
                    <Text fontSize="sm" fontWeight="bold" color="#162237" noOfLines={2} mb={1}>
                      {item.bookTitle}
                    </Text>
                    <Text fontSize="xs" color="gray.500" mb={3}>
                      {item.author || "Legal Software Edition"}
                    </Text>
                    <HStack justify="space-between" align="center">
                      <Text fontSize="sm" fontWeight="extrabold" color="#162237">
                        ₹{Number(item.price || 0).toLocaleString('en-IN')}
                      </Text>
                      <Button size="xs" colorScheme="blue" variant="outline">
                        View Details
                      </Button>
                    </HStack>
                  </Box>
                </Box>
              ))}
          </Slider>
        </Box>
      </Container>
    </Box>
  );
}

export default LMS;
